import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import useUser from "../hooks/useUser";
import useUserStore from "../store/userStore";
import UserInformation from "../components/UserInformation";
import MedalModal from "../components/MedalModal";
import UserMedal from "../interfaces/userMedal";
import { ASSETS_URL } from "../util/constants";
import type Community from "../interfaces/community";
import "./Profile.css";

type BadgeCategory = "pokedex" | "medals" | "type-captures";

function loadCollectionables(badges: UserMedal[], visitor: boolean, onMedalClick: (badge: UserMedal) => void) {
  badges = badges.filter((badge) => badge.quantity > 0);
  if (!badges || badges.length === 0) {
    return (
      <p className="profile-empty-state">
        This trainer has no collectionables yet.
      </p>
    );
  }

  return badges.map((badge) => {
    let pathImage = `${ASSETS_URL}${badge.medal.pathImage}`;
    return (
      <div
        key={badge.medal.id}
        className="text-center"
        style={{ cursor: "pointer" }}
        onClick={() => onMedalClick(badge)}
      >
        <img
          src={pathImage}
          alt={badge.medal.name}
          style={{ width: "4.5rem" }}
          className="mb-1"
        />
      </div>
    );
  });
}

const getCommunityLogo = (community?: Community | null) => {
  const logoPath = community?.logo || community?.pathImage;

  if (!logoPath) {
    return "/logo.png";
  }

  if (logoPath.startsWith("http")) {
    return logoPath;
  }

  return `${ASSETS_URL}${logoPath.startsWith("/") ? logoPath : `/${logoPath}`}`;
};

/**
 * Renders a grid of badges using your original logic.
 * @param {UserMedal[]} badges - The array of badge data to display.
 * @param {boolean} visitor - Flag to adjust asset paths.
 */
function loadBadges(badges: UserMedal[], visitor: boolean, onMedalClick: (badge: UserMedal) => void) {
  if (!badges || badges.length === 0) {
    return (
      <p className="profile-empty-state">
        There is no data for this user to display.
      </p>
    );
  }

  return badges.map((badge) => {
    let pathImage = `${ASSETS_URL}/${badge.medal.pathImage}`;
    if (badge.quantity >= badge.medal.amountForPlatinum)
      pathImage += "_Platinum.png";
    else if (badge.quantity >= badge.medal.amountForGold)
      pathImage += "_Gold.png";
    else if (badge.quantity >= badge.medal.amountForSilver)
      pathImage += "_Silver.png";
    else if (badge.quantity >= badge.medal.amountForBronze)
      pathImage += "_Bronze.png";
    else pathImage += "_Shadow.png";

    return (
      <div
        key={badge.medal.id}
        className="text-center"
        style={{ cursor: "pointer" }}
        onClick={() => onMedalClick(badge)}
      >
        <img
          src={pathImage}
          alt={badge.medal.name}
          style={{ width: "4.5rem" }}
          className="mb-1"
        />
        <p style={{ margin: 0, fontSize: "0.8rem", color: "#c2c2c2" }}>
          {badge.quantity.toLocaleString("de-DE")}
        </p>
      </div>
    );
  });
}

function Profile() {
  const loggedInUsername = useUserStore((state) => state.username);
  const { username } = useParams();

  const [activeTab, setActiveTab] = useState<BadgeCategory>("pokedex");
  const [selectedMedal, setSelectedMedal] = useState<UserMedal | null>(null);

  const visitor = !!username;
  const loadUsername = username || loggedInUsername;

  const {
    data: user,
    isLoading: isLoadingUser,
    error: errorUser,
  } = useUser(loadUsername);

  if (isLoadingUser)
    return <p className="text-center mt-5">Loading Profile...</p>;
  if (errorUser || !user)
    return (
      <p className="text-center mt-5">User not found or error loading data.</p>
    );

  const medals = user.medals;

  const collectionables = medals
    ? medals.filter((m) => m.medal.medalType.slug === "collectionables")
    : [];

  const otherBadges: Record<BadgeCategory, UserMedal[]> = {
    pokedex: medals
      ? medals.filter((m) => m.medal.medalType.slug === "pokedex")
      : [],
    medals: medals
      ? medals.filter((m) => m.medal.medalType.slug === "medals")
      : [],
    "type-captures": medals
      ? medals.filter((m) => m.medal.medalType.slug === "type-captures")
      : [],
  };

  

  const handleMedalClick = (badge: UserMedal) => {
    setSelectedMedal(badge);
  };

  const handleCloseModal = () => {
    setSelectedMedal(null);
  };

  return (
    <>
      <div className="profile-page">
        <div className="profile-block profile-hero">
          <UserInformation user={user} visitor={visitor} />

          <div className="profile-community-panel">
            <div className="profile-community-logo-wrap">
              <img
                src={getCommunityLogo(user.community)}
                alt={user.community?.name || "Community"}
                className="profile-community-logo"
              />
            </div>
            <div className="profile-community-copy">
              <span className="profile-kicker">Community</span>
              {user.community ? (
                <>
                  <h3>{user.community.name}</h3>
                  <p>
                    {user.community.description || "No description provided."}
                  </p>
                  <div className="profile-community-meta">
                    {user.community.ownerUsername && (
                      <span>Owner: {user.community.ownerUsername}</span>
                    )}
                    <span>
                      {user.community.memberCount ?? 0}{" "}
                      {(user.community.memberCount ?? 0) === 1
                        ? "member"
                        : "members"}
                    </span>
                  </div>
                  {user.community.id && (
                    <Link
                      className="profile-community-link"
                      to={`/community/${user.community.id}`}
                    >
                      View community
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <h3>No community yet</h3>
                  <p>This trainer is not part of a community.</p>
                  {!visitor && (
                    <Link className="profile-community-link" to="/community">
                      Find a community
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="profile-block">
          <div className="profile-section-heading">
            <h3 className="profile-section-title">Collectionables</h3>
            <Link className="profile-collectionables-link" to="/collectionables">
              View all
            </Link>
          </div>
          <hr className="profile-divider" />
          <div className="profile-badge-grid">
            {isLoadingUser ? (
              <p>Loading...</p>
            ) : (
              loadCollectionables(collectionables, visitor, handleMedalClick)
            )}
          </div>
        </div>

        <div className="profile-block">
          <div className="profile-tabs" role="tablist" aria-label="Badge categories">
            <button
              type="button"
              className={activeTab === "pokedex" ? "active" : ""}
              onClick={() => setActiveTab("pokedex")}
            >
              Pokedex
            </button>
            <button
              type="button"
              className={activeTab === "medals" ? "active" : ""}
              onClick={() => setActiveTab("medals")}
            >
              Medals
            </button>
            <button
              type="button"
              className={activeTab === "type-captures" ? "active" : ""}
              onClick={() => setActiveTab("type-captures")}
            >
              Type Captures
            </button>
          </div>
          <hr className="profile-divider" />
          <div className="profile-badge-grid">
            {isLoadingUser ? (
              <p>Loading...</p>
            ) : errorUser ? (
              <p>Error loading badges</p>
            ) : (
              loadBadges(otherBadges[activeTab], visitor, handleMedalClick)
            )}
          </div>
        </div>
      </div>
      {selectedMedal && (
        <MedalModal
          badge={selectedMedal}
          onClose={handleCloseModal}
          visitor={visitor}
        />
      )}
    </>
  );
}

export default Profile;
